import dayjs from "dayjs";

import {
  ActionTriggerEvent,
  ApplicableFor,
  Channel,
  GeoTriggerEvent,
  SendTo,
  Template,
  TimeBasedCondition,
  TimeBasedDelayedFrom,
  TriggerType,
} from "@web-app/types";

export const messageShortcuts = {
  BusinessName: {
    display: "Business Name",
    sample: "Anytown Delivery",
  },
  CustomerName: {
    display: "Customer Name",
    sample: "Jane Doe",
  },
  DelayMinutes: {
    display: "Delay Threshold (min)",
    sample: "45",
  },
  DeliveryWindow: {
    display: "Delivery Window",
    sample: "10:00 AM - 12:00 PM",
  },
  DriverName: {
    display: "Driver Name",
    sample: "Fred Nerk",
  },
  DropoffAddress: {
    display: "Dropoff Address",
    sample: "123 Main St, Anytown, USA",
  },
  DropoffCity: {
    display: "Dropoff City",
    sample: "Anytown",
  },
  DropoffState: {
    display: "Dropoff State",
    sample: "Texas",
  },
  OrderEta: {
    display: "Order ETA",
    sample: "10:05 AM",
  },
  OrderNumber: {
    display: "Order Number",
    sample: `${dayjs().format("YYYYMMDD")}-123`,
  },
  PickupAddress: {
    display: "Pickup Address",
    sample: "456 Main St, Anytown, USA",
  },
  PickupCity: {
    display: "Pickup City",
    sample: "Anytown",
  },
  PickupState: {
    display: "Pickup State",
    sample: "Texas",
  },
  TrackingUrl: {
    display: "Tracking URL",
    sample: "https://<custom_domain>.fulflld.live/t/o/aYn9KjJ6pA3oUsPT",
  },
};

export const messageShortcutsForOrderReportCreation = {
  ItemCount: {
    display: "Item Count",
    sample: "10",
  },
  OrderId: {
    display: "Order ID",
    sample: "XYZ-324232",
  },
  ReportDescription: {
    display: "Report Description",
    sample: "Order has been shipped.",
  },
  ReportTopic: {
    display: "Report Topic",
    sample: "Order Shipped",
  },
  Value: {
    display: "Value",
    sample: "100",
  },
};

export const slackExtraShortcuts = [
  { display: "@here", text: "<!here>" },
  { display: "@channel", text: "<!channel>" },
  { display: "✅", text: "✅" },
  { display: "👍", text: "👍" },
];

export const getMessagePreview = (
  template: Template,
  channel: Channel,
  slug: string,
  customDomain: string,
  delayMinutes?: number,
  templateMessageShortcuts: Record<
    string,
    {
      display: string;
      sample: string;
    }
  > = {},
) => {
  let previewMessage =
    channel === Channel.SMS ? template.sms.message : template.slack.message;
  Object.entries(templateMessageShortcuts).forEach(([key, value]) => {
    // Escape special regex characters in the key and wrap with {{. }}
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const placeholderPattern = `\\{\\{\\.${escapedKey}\\}\\}`;

    let sample = value.sample;
    if (escapedKey === "OrderNumber") {
      sample = `${slug}-${sample}`;
    }

    if (escapedKey === "TrackingUrl" && customDomain) {
      sample = sample.replace(/<custom_domain>/g, customDomain);
    }

    // Show the delay threshold actually configured on this rule's trigger,
    // not the generic placeholder sample — this is a real per-rule setting,
    // not illustrative sample data like the other variables.
    if (escapedKey === "DelayMinutes" && delayMinutes !== undefined) {
      sample = `${delayMinutes}`;
    }

    previewMessage = previewMessage.replace(
      new RegExp(placeholderPattern, "g"),
      sample,
    );
  });
  return previewMessage;
};

export const getTriggerTypeForOptions = (
  applicableFor: ApplicableFor,
  channel: Channel,
) => {
  let options = [
    {
      label: "Action-Based",
      value: TriggerType.ACTION,
    },
    {
      label: "Geo-Based",
      value: TriggerType.GEO,
    },
    {
      label: "Route Progress",
      value: TriggerType.PROGRESS,
    },
    {
      label: "Time-Based",
      value: TriggerType.TIME,
    },
  ];

  if (applicableFor === ApplicableFor.P2P_ORDER) {
    options = options.filter((option) => option.value !== TriggerType.PROGRESS);
  }

  if (channel === Channel.EMAIL) {
    options = options.filter((option) => option.value === TriggerType.ACTION);
  }

  return options;
};

export const applicableForOptions = [
  {
    label: "Point-to-Point Orders",
    value: ApplicableFor.P2P_ORDER,
  },
  {
    label: "Routes",
    value: ApplicableFor.ROUTES,
  },
];

export const orderDelayedFrom = [
  {
    label: "Scheduled Start Time",
    value: TimeBasedDelayedFrom.SCHEDULED_START_TIME,
  },
  {
    label: "Scheduled End Time",
    value: TimeBasedDelayedFrom.SCHEDULED_END_TIME,
  },
];

export const getApplicableForOptions = (channel: Channel) => {
  if (channel === Channel.EMAIL) {
    return applicableForOptions.filter(
      (option) => option.value !== ApplicableFor.P2P_ORDER,
    );
  }

  return applicableForOptions;
};

export const sendToOptions = [
  {
    label: "Pickup Contact",
    value: SendTo.PICKUP_CONTACT,
  },
  {
    label: "Dropoff Contact",
    value: SendTo.DROPOFF_CONTACT,
  },
];

export const actionTriggerOptionsForP2P = [
  {
    label: "Order Created",
    value: ActionTriggerEvent.ORDER_CREATED,
  },
  {
    label: "Order Assigned",
    value: ActionTriggerEvent.ORDER_ASSIGNED,
  },
  {
    label: "Order Unassigned",
    value: ActionTriggerEvent.ORDER_UNASSIGNED,
  },
  {
    label: "Started Pickup",
    value: ActionTriggerEvent.PICKUP_IN_PROGRESS,
  },
  {
    label: "Started Drop-off",
    value: ActionTriggerEvent.DROPOFF_IN_PROGRESS,
  },
  {
    label: "Drop-off Completed",
    value: ActionTriggerEvent.ORDER_COMPLETED,
  },
  {
    label: "Order Report Created",
    value: ActionTriggerEvent.ORDER_REPORT_CREATED,
  },
];

export const actionTriggerOptionsForRoutes = [
  {
    label: "Order Created",
    value: ActionTriggerEvent.ORDER_CREATED,
  },
  {
    label: "Order Started",
    value: ActionTriggerEvent.ROUTE_ORDER_STARTED,
  },
  {
    label: "Order Completed",
    value: ActionTriggerEvent.ORDER_COMPLETED,
  },
  {
    label: "Route Assigned",
    value: ActionTriggerEvent.ROUTE_ASSIGNED,
  },
  {
    label: "Route Unassigned",
    value: ActionTriggerEvent.ROUTE_UNASSIGNED,
  },
  {
    label: "Route Completed",
    value: ActionTriggerEvent.ROUTE_COMPLETED,
  },
  {
    label: "Order Report Created",
    value: ActionTriggerEvent.ORDER_REPORT_CREATED,
  },
];

export const geoTriggerOptionsForP2P = [
  {
    label: "Drop-off Location",
    value: GeoTriggerEvent.DROPOFF,
  },
  {
    label: "Pick-up Location",
    value: GeoTriggerEvent.PICKUP,
  },
];

export const geoTriggerOptionsForRoutes = [
  {
    label: "Driver Nearby Destination",
    value: GeoTriggerEvent.NEARBY,
  },
];

export const timeTriggerOptions = [
  {
    label: "At a specific time on delivery day",
    value: TimeBasedCondition.SPECIFIC_TIME,
  },
  {
    label: "Order delayed by",
    value: TimeBasedCondition.DELAYED,
  },
];
